/**
 * DataManager
 * GraphQL fetching with cursor pagination and prefetch logic
 * History-only storage: products stored in GridInstance/DataManager state, then in history
 * No cache duplication - history is the single source of truth
 */
class DataManager {
  constructor(options = {}) {
    // Configuration
    this.batchSize = options.batchSize || 15;
    this.prefetchThreshold = options.prefetchThreshold || 0.8;

    // Storefront API config
    this.endpoint = null;
    this.token = null;

    // State
    this.collectionHandle = null;
    this.cacheKey = null; // Kept for compatibility but not used for storage
    this.urlStateManager = null;

    // Fetch state
    this.isFetching = false;
    this.fetchQueue = [];
    
    // Product state (history-only, no cache duplication)
    this.loadedProducts = []; // All loaded products
    this.lastCursor = null; // Last cursor for pagination
    this.hasMore = false; // Whether more products available
    this.filters = []; // Current filters
    this.totalCount = 0; // Total products count

    // Callbacks
    this.onProductsLoaded = options.onProductsLoaded || null;
    this.onFetchStart = options.onFetchStart || null;
    this.onFetchEnd = options.onFetchEnd || null;
    this.onError = options.onError || null;

    this.isInitialized = false;
  }

  /**
   * Initialize the data manager
   * @param {string} collectionHandle - Collection handle
   * @param {URLStateManager} urlStateManager - URL state manager
   * @param {string} [gridId] - Grid ID for feed-specific caching
   * @param {string} [feedType] - Feed type: "collection" or "recommendation"
   */
  initialize(collectionHandle, urlStateManager, gridId = null, feedType = null) {
    if (!window.storefront) {
      return false;
    }

    this.endpoint = window.storefront.endpoint;
    this.token = window.storefront.token;

    if (!this.endpoint || !this.token) {
      return false;
    }

    this.collectionHandle = collectionHandle;
    this.urlStateManager = urlStateManager;

    // Generate feed-specific cache key if gridId and feedType provided
    if (gridId && feedType) {
      this.cacheKey = urlStateManager.generateCacheKey(
        window.location.href,
        gridId,
        feedType,
        collectionHandle
      );
    } else {
      // Legacy format for backward compatibility
      this.cacheKey = urlStateManager.generateCacheKey();
    }

    this.isInitialized = true;
    return true;
  }

  /**
   * Execute a GraphQL query
   */
  async executeQuery(query, variables = {}) {
    const response = await fetch(this.endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": this.token,
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) {
      throw new Error(
        `GraphQL request failed: ${response.status} ${response.statusText}`
      );
    }

    const result = await response.json();

    if (result.errors) {
      throw new Error(result.errors[0]?.message || "GraphQL query failed");
    }

    return result.data;
  }

  /**
   * Deduplicate filter values
   * Shopify can return duplicate filter values which causes incorrect counts
   */
  deduplicateFilters(filters) {
    if (!filters || !Array.isArray(filters)) return [];

    return filters.map((filter) => {
      const uniqueValues = filter.values.filter(
        (value, index, array) =>
          array.findIndex((v) => v.id === value.id) === index
      );
      return { ...filter, values: uniqueValues };
    });
  }

  /**
   * Calculate total product count from filter data
   * Uses product_type filter preferentially, falls back to availability
   */
  calculateTotalFromFilters(filters) {
    if (!filters || !Array.isArray(filters) || filters.length === 0) {
      return 0;
    }

    const dedupedFilters = this.deduplicateFilters(filters);

    // Prefer product_type filter for accurate count
    const productTypeFilter = dedupedFilters.find(
      (f) => f.id === "filter.p.product_type"
    );
    if (productTypeFilter?.values?.length > 0) {
      const total = productTypeFilter.values.reduce(
        (sum, v) => sum + (v.count || 0),
        0
      );
      return total;
    }

    // Fallback to availability filter
    const availabilityFilter = dedupedFilters.find(
      (f) => f.id === "filter.v.availability"
    );
    if (availabilityFilter?.values?.length > 0) {
      const total = availabilityFilter.values.reduce(
        (sum, v) => sum + (v.count || 0),
        0
      );
      return total;
    }

    // Last resort: try vendor filter
    const vendorFilter = dedupedFilters.find((f) => f.id === "filter.p.vendor");
    if (vendorFilter?.values?.length > 0) {
      const total = vendorFilter.values.reduce(
        (sum, v) => sum + (v.count || 0),
        0
      );
      return total;
    }

    return 0;
  }

  /**
   * Fetch initial batch of products for a collection
   * History-only storage: always fetches fresh, no cache reads
   */
  async fetchInitialBatch() {
    if (!this.isInitialized) {
      throw new Error("DataManager not initialized");
    }

    // Reset state for fresh fetch
    this.loadedProducts = [];
    this.lastCursor = null;
    this.hasMore = false;
    this.filters = [];
    this.totalCount = 0;

    // Fetch from API (history-only, no cache)
    return this.fetchCollectionProducts(null);
  }

  /**
   * Fetch collection products with cursor pagination
   */
  async fetchCollectionProducts(cursor = null) {
    if (this.isFetching) {
      return new Promise((resolve, reject) => {
        this.fetchQueue.push({ cursor, resolve, reject });
      });
    }

    this.isFetching = true;
    if (this.onFetchStart) this.onFetchStart();

    try {
      const { sortKey, reverse } = this.urlStateManager.getGraphQLSort();
      const filters = this.urlStateManager.getGraphQLFilters();

      const variables = {
        handle: this.collectionHandle,
        first: this.batchSize,
        after: cursor,
        sortKey: sortKey,
        reverse: reverse,
      };

      // Only include filters if there are any
      if (filters.length > 0) {
        variables.filters = filters;
      }

      const data = await this.executeQuery(
        GraphQLQueries.COLLECTION_PRODUCTS,
        variables
      );

      if (!data.collection) {
        throw new Error("Collection not found: " + this.collectionHandle);
      }

      const { products } = data.collection;
      const slimmedProducts = CacheManager.slimProducts(products.nodes);

      // Calculate total count from filters
      const dedupedFilters = this.deduplicateFilters(products.filters);
      const totalCount = this.calculateTotalFromFilters(dedupedFilters);

      // Update internal state (history-only, no cache writes)
      if (cursor === null) {
        // First batch - reset state
        this.loadedProducts = slimmedProducts;
        this.filters = dedupedFilters;
        this.totalCount = totalCount;
      } else {
        // Append to existing products
        this.loadedProducts = [...this.loadedProducts, ...slimmedProducts];
      }
      
      this.lastCursor = products.pageInfo.endCursor;
      this.hasMore = products.pageInfo.hasNextPage;

      const result = {
        products: slimmedProducts,
        totalCount: totalCount,
        hasMore: products.pageInfo.hasNextPage,
        cursor: products.pageInfo.endCursor,
        filters: dedupedFilters,
        fromCache: false,
      };

      // Notify listeners with all products from state (not cache)
      if (this.onProductsLoaded) {
        this.onProductsLoaded({
          newProducts: slimmedProducts,
          allProducts: this.loadedProducts, // From state, not cache
          totalCount: this.totalCount,
          loadedCount: this.loadedProducts.length,
          hasMore: this.hasMore,
          cursor: this.lastCursor,
          filters: this.filters,
        });
      }

      return result;
    } catch (error) {
      if (this.onError) this.onError(error);
      throw error;
    } finally {
      this.isFetching = false;
      if (this.onFetchEnd) this.onFetchEnd();
      this.processQueue();
    }
  }

  /**
   * Fetch next batch of products
   * Reads cursor from internal state (history-only, no cache)
   */
  async fetchNextBatch() {
    if (!this.hasMore || !this.lastCursor) {
      return null;
    }

    return this.fetchCollectionProducts(this.lastCursor);
  }

  /**
   * Check if prefetch should be triggered (history-only, no cache)
   */
  shouldPrefetch(currentIndex) {
    if (!this.hasMore) return false;
    
    const loadedCount = this.loadedProducts.length;
    if (loadedCount === 0) return true;

    const threshold = loadedCount * this.prefetchThreshold;
    return currentIndex >= threshold;
  }

  /**
   * Prefetch next batch if needed
   */
  async prefetchIfNeeded(currentIndex) {
    // No prefetching in recommendations mode
    if (this.mode === "recommendations") return;
    if (this.isFetching) return;

    if (this.shouldPrefetch(currentIndex)) {
      await this.fetchNextBatch();
    }
  }

  /**
   * Process queued fetch requests
   */
  processQueue() {
    if (this.fetchQueue.length === 0) return;

    const next = this.fetchQueue.shift();
    this.fetchCollectionProducts(next.cursor)
      .then(next.resolve)
      .catch(next.reject);
  }

  /**
   * Reset for new sort/filter
   * Resets state for history-only storage
   */
  reset(newCacheKey) {
    this.cacheKey = newCacheKey;
    this.fetchQueue = [];
    this.isFetching = false;
    // Reset product state
    this.loadedProducts = [];
    this.lastCursor = null;
    this.hasMore = false;
    this.filters = [];
    this.totalCount = 0;
  }

  /**
   * Get all loaded products from state (history-only, no cache)
   * @returns {Array} Array of slimmed product objects
   */
  getLoadedProducts() {
    return this.loadedProducts || [];
  }

  /**
   * Get loaded count
   */
  getLoadedCount() {
    return this.loadedProducts.length || 0;
  }

  /**
   * Get total count
   */
  getTotalCount() {
    return this.totalCount || 0;
  }

  /**
   * Get filters
   */
  getFilters() {
    return this.filters || [];
  }

  /**
   * Check if collection loading is complete
   */
  isComplete() {
    return !this.hasMore;
  }

  /**
   * Get current fetch state (history-only, no cache)
   */
  getState() {
    return {
      isInitialized: this.isInitialized,
      isFetching: this.isFetching,
      collectionHandle: this.collectionHandle,
      cacheKey: this.cacheKey, // Kept for compatibility
      loadedCount: this.loadedProducts.length || 0,
      totalCount: this.totalCount || 0,
      complete: !this.hasMore,
      hasMore: this.hasMore,
      lastCursor: this.lastCursor,
      hasFilters: (this.filters?.length || 0) > 0,
    };
  }

  /**
   * Fetch product recommendations
   */
  async fetchRecommendations(productId) {
    try {
      const data = await this.executeQuery(
        GraphQLQueries.PRODUCT_RECOMMENDATIONS,
        { productId }
      );

      if (!data.productRecommendations) {
        return [];
      }

      return CacheManager.slimProducts(data.productRecommendations);
    } catch (error) {
      return [];
    }
  }

  /**
   * Fetch expanded recommendations (one level deep)
   * Gets recommendations for a product, then for each recommended product
   * History-only storage: stores in state, no cache
   * @param {string} productId - Shopify GID for the product
   * @param {boolean} useCache - Deprecated: always fetches fresh (history-only)
   * @returns {Promise<Array>} Deduplicated array of recommended products
   */
  async fetchExpandedRecommendations(productId, useCache = false) {
    // History-only: if we already have products in state, return them
    // (for restoration, products come from history, not here)
    if (this.loadedProducts && this.loadedProducts.length > 0) {
      return this.loadedProducts;
    }

    try {
      // Step 1: Get initial recommendations (up to 10)
      const initialRecs = await this.fetchRecommendations(productId);

      if (!initialRecs || initialRecs.length === 0) {
        return [];
      }

      // Step 2: Fetch recommendations for each initial recommendation in parallel
      const secondaryPromises = initialRecs.map((rec) =>
        this.fetchRecommendations(rec.id).catch((err) => {
          return [];
        })
      );

      const secondaryResults = await Promise.all(secondaryPromises);

      // Step 3: Merge and deduplicate
      const allProducts = [...initialRecs];
      const seenIds = new Set(allProducts.map((p) => p.id));

      // Also exclude the original product
      seenIds.add(productId);

      for (const recArray of secondaryResults) {
        for (const product of recArray) {
          if (!seenIds.has(product.id)) {
            seenIds.add(product.id);
            allProducts.push(product);
          }
        }
      }

      // Store in state (history-only, no cache)
      this.loadedProducts = allProducts;
      this.hasMore = false; // Recommendations are fully loaded
      this.totalCount = allProducts.length;

      return allProducts;
    } catch (error) {
      if (this.onError) this.onError(error);
      return [];
    }
  }

  /**
   * Initialize for recommendations mode
   * @param {string} productId - The product ID to get recommendations for
   */
  initializeForRecommendations(productId, gridId = null) {
    if (!window.storefront) {
      return false;
    }

    this.endpoint = window.storefront.endpoint;
    this.token = window.storefront.token;
    this.productId = productId;
    this.mode = "recommendations";

    // Generate feed-specific cache key if gridId provided
    if (gridId) {
      this.cacheKey = `cache:${gridId}:recommendation:${productId}`;
    } else {
      // Legacy format for backward compatibility
      this.cacheKey = `cache:recommendations:${productId}`;
    }

    this.isInitialized = true;
    return true;
  }

  /**
   * Initialize for static collection mode
   * Static collections use simple cache keys: cache:{gridId}:collection:{handle}
   * These keys do NOT include URL pathname or query params (unlike collection_page mode)
   * 
   * @param {string} collectionHandle - The collection handle
   * @param {string} [gridId] - Grid ID for feed-specific caching
   */
  initializeForStaticCollection(collectionHandle, gridId = null) {
    if (!window.storefront) {
      return false;
    }

    this.endpoint = window.storefront.endpoint;
    this.token = window.storefront.token;
    this.collectionHandle = collectionHandle;
    this.mode = "static_collection";

    // Create a simple URL state manager that ignores URL params
    // Static collections are independent of the current page URL
    this.urlStateManager = {
      generateCacheKey: () => {
        if (gridId) {
          // Simple format: cache:gridId:collection:handle
          // NO URL pathname, NO query params
          return `cache:${gridId}:collection:${collectionHandle}`;
        }
        return `cache:static:${collectionHandle}`;
      },
      getGraphQLSort: () => ({ sortKey: "COLLECTION_DEFAULT", reverse: false }),
      getGraphQLFilters: () => [], // Static collections don't use URL filters
    };

    this.cacheKey = this.urlStateManager.generateCacheKey();
    this.isInitialized = true;
    return true;
  }
}

// Export for module usage
if (typeof module !== "undefined" && module.exports) {
  module.exports = DataManager;
}
